/* eslint-disable jsx-a11y/media-has-caption */
import React, { useRef, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useCopilotRepositoryContext } from '@bingads-webui/inline-copilot';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Panel, PanelType, DefaultButton } from '@bingads-webui-react/fabric-control';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { ReactTemplate } from '@bingads-webui-react/react-template';

import { VideoSuggestionCard } from './video-suggestion-card';

const getPanelStyles = (components, { palette = {} } = {}) => ({
  panel: {
    top: 60,
  },
  subtitle: {
    paddingTop: 22,
    color: palette.neutralDark,
  },
  panelHeader: {
    paddingLeft: 24,
    paddingRight: 24,
    alignSelf: 'justify-start',
    flexGrow: 1,
  },
  panelTitle: {
    fontSize: 20,
    fontWeight: 600,
    display: 'inline-block',
    lineHeight: '27px',
  },
  contentHeading: {
    paddingTop: 17,
    fontSize: '12px',
    paddingBottom: 20,
  },
  gridContainer: {
    display: 'grid',
    columnGap: '16px',
    rowGap: '16px',
    gridTemplateColumns: '430px 430px',
  },
});

export const VideoRecommendationsPanel = ({
  i18n, isOpen, close, onSelectVideoSuggestion, permissions,
}) => {
  const classes = useDefaultStyles(getPanelStyles);
  const contentRef = useRef(null);
  const { finalUrl } = useCopilotRepositoryContext();
  const [recommendationContext] = useContext(RecommendationContext);
  const {
    videoAssets,
  } = recommendationContext || {};
  const isVideoAsAnAssetEnabled = permissions.dynamic.VideoAsAnAsset && !permissions.isAmplifyEnabled;

  const onDismiss = useCallback((evt) => {
    const target = evt && evt.currentTarget;
    if (evt.key === 'Escape' || (contentRef.current && contentRef.current.closest(`.${classes.panel}`).contains(target))) {
      close();
    }
  }, [classes.panel, close]);

  const onRenderFooterContent = () => (
    <DefaultButton
      className={classnames('cancel-button', 'all-video-reco-cancel-button')}
      onClick={() => close()}
      text={i18n.getString(_TL_('Cancel'))}
    />
  );

  const onSelection = (index) => {
    onSelectVideoSuggestion(index);
    close();
  };

  return (
    <Panel
      isOpen={isOpen}
      type={PanelType.custom}
      onDismiss={onDismiss}
      headerText={i18n.getString(_TL_('Recommended videos'))}
      className={classnames(classes.panel, 'view-all-video-recommendations-panel')}
      isFooterAtBottom
      customWidth="940px"
      closeButtonAriaLabel={i18n.getString(_TL_('close'))}
      onRenderFooterContent={onRenderFooterContent}
    >
      <FluentProvider theme={webLightTheme}>
        <div ref={contentRef} role="heading" aria-level="2" className={classes.contentHeading}>
          <ReactTemplate
            model={{
              strong: text => <strong>{text}</strong>,
              finalUrl,
            }}
            template={i18n.getString(_TL_('{{strong}}Final URL:{{/strong}} {{finalUrl}}'))}
          />
        </div>
        <div className={classnames(classes.gridContainer, 'video-recommendation-content')}>
          {videoAssets &&
            videoAssets.map((videoAsset, index) => (<VideoSuggestionCard
              key={videoAsset.suggestionId}
              videoAsset={videoAsset}
              onSelectVideoSuggestion={() => onSelection(index)}
              resizeHeight={242}
              i18n={i18n}
              showQuality={isVideoAsAnAssetEnabled}
            />))
          }
        </div>
      </FluentProvider>
    </Panel>
  );
};

VideoRecommendationsPanel.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  onSelectVideoSuggestion: PropTypes.func.isRequired,
  permissions: PropTypes.shape({}).isRequired,
};
